import React from 'react'
import ReactDOM from 'react-dom/client'
import { WindowContextProvider } from './components/layout/Titlebar'
import { ModeProvider } from './contexts/ModeProvider'
import { LanguageProvider } from './contexts/LanguageProvider'
import { DisplayModeProvider } from './contexts/DisplayModeProvider'
import { useTranslation } from 'react-i18next'
import { supabase } from './lib/supabase/client'
import { SkipLink } from './components/ui/SkipLink'
import App from './App'
import { ErrorBoundary } from './components/common/ErrorBoundary'
import { initAnalytics } from './lib/analytics'
import { initSentry } from './lib/sentry'
import { bootstrapProfileSyncBridge } from './lib/profile'
import { SmileyIcon } from './components/icons/SmileyIcon'
import './styles/tailwind.css' // MUST be first — Tailwind preflight + utilities, depended on by shadcn primitives
import './styles/app.scss'
import './styles/globals.scss'
import './i18n/config'

// Note: Supabase auth token handling is done in index.html (inline script)
// This ensures the hash is cleaned BEFORE React Router initializes

// Platform CSS class for native cursor behavior (Electron vs Web)
const isElectronPlatform = typeof window !== 'undefined' && 'api' in window && window.api !== undefined
document.documentElement.classList.add(isElectronPlatform ? 'platform-electron' : 'platform-web')

// Initialize PostHog analytics (no-op if env vars not set)
try {
  initAnalytics()
} catch (e) {
  console.error('[Analytics] Failed to initialize:', e)
}

// Initialize Sentry for error tracking and user feedback (no-op if DSN not set)
initSentry()

// Bridge cross-window profile-update broadcasts into the in-process event
// bus so consumers (sidebar avatar, user-menu label) listen to a single
// source. See `lib/profile/bootstrapProfileSyncBridge` and the main-process
// `user-profile-updated` IPC handler.
bootstrapProfileSyncBridge()


// Skip Button Component for Titlebar (only visible on setup page when not logged in)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SkipButton = () => {
  const { t } = useTranslation()
  const [isHidden, setIsHidden] = React.useState(false)
  // Initialize currentPath based on platform (HashRouter vs BrowserRouter)
  const [currentPath, setCurrentPath] = React.useState(() => {
    const isElectron = typeof window !== 'undefined' && 'api' in window && window.api !== undefined
    if (isElectron) {
      const hash = window.location.hash
      return hash.startsWith('#') ? hash.substring(1) : hash || '/'
    }
    return window.location.pathname
  })
  const [isAuthenticated, setIsAuthenticated] = React.useState(false)
  const [authView, setAuthView] = React.useState<string>('register')

  // Check authentication status
  React.useEffect(() => {
    const checkAuth = async () => {
      if (!supabase) {
        setIsAuthenticated(false)
        return
      }

      const { data } = await supabase.auth.getSession()
      setIsAuthenticated(!!data.session)
    }

    checkAuth()

    // Listen for auth changes
    if (supabase) {
      const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
        setIsAuthenticated(!!session)
      })

      return () => {
        authListener?.subscription.unsubscribe()
      }
    }
  }, [])

  // Track route changes
  React.useEffect(() => {
    const handleRouteChange = () => {
      // In Electron (HashRouter), use hash as path
      // In Web (BrowserRouter), use pathname
      const isElectron = typeof window !== 'undefined' && 'api' in window && window.api !== undefined
      if (isElectron) {
        // HashRouter: Extract path from hash (e.g., "#/setup-profile" -> "/setup-profile")
        const hash = window.location.hash
        const path = hash.startsWith('#') ? hash.substring(1) : hash
        setCurrentPath(path || '/')
      } else {
        // BrowserRouter: Use pathname directly
        setCurrentPath(window.location.pathname)
      }
    }

    // Initial path check
    handleRouteChange()

    // Listen for navigation changes
    window.addEventListener('popstate', handleRouteChange)
    window.addEventListener('hashchange', handleRouteChange) // HashRouter changes

    // Also listen for pushstate/replacestate (React Router)
    const originalPushState = window.history.pushState
    const originalReplaceState = window.history.replaceState

    window.history.pushState = function(...args) {
      originalPushState.apply(window.history, args)
      handleRouteChange()
    }

    window.history.replaceState = function(...args) {
      originalReplaceState.apply(window.history, args)
      handleRouteChange()
    }

    return () => {
      window.removeEventListener('popstate', handleRouteChange)
      window.removeEventListener('hashchange', handleRouteChange)
      window.history.pushState = originalPushState
      window.history.replaceState = originalReplaceState
    }
  }, [])

  React.useEffect(() => {
    const handleShowDialog = () => setIsHidden(true)
    const handleHideDialog = () => setIsHidden(false)

    window.addEventListener('setup-skip-dialog-show', handleShowDialog)
    window.addEventListener('setup-skip-dialog-hide', handleHideDialog)

    return () => {
      window.removeEventListener('setup-skip-dialog-show', handleShowDialog)
      window.removeEventListener('setup-skip-dialog-hide', handleHideDialog)
    }
  }, [])

  // Listen for auth view changes (forgotPassword, resetPassword)
  React.useEffect(() => {
    const handleAuthViewChange = (e: Event) => {
      const customEvent = e as CustomEvent<string>
      setAuthView(customEvent.detail)
    }

    window.addEventListener('setup-auth-view-changed', handleAuthViewChange as EventListener)

    return () => {
      window.removeEventListener('setup-auth-view-changed', handleAuthViewChange as EventListener)
    }
  }, [])

  const handleClick = () => {
    // Dispatch custom event for SetupPage to listen to
    window.dispatchEvent(new Event('setup-skip-clicked'))
  }

  // Only show on setup pages (root path, purpose, and intent) AND when user is NOT logged in
  const isOnSetupPage = currentPath === '/' || currentPath === '/setup-purpose' || currentPath === '/setup-intent'
  const offlineSetupCompleted = localStorage.getItem('offlineSetupCompleted') === 'true'
  const isPasswordRecoveryView = authView === 'forgotPassword' || authView === 'resetPassword'

  if (!isOnSetupPage || isHidden || isAuthenticated || offlineSetupCompleted || isPasswordRecoveryView) {
    return null
  }

  return (
    <SkipLink onClick={handleClick}>
      {t('setup.skip')}
    </SkipLink>
  )
}

// BackButton Component - Shows "Zurück" button in titlebar (password reset flows)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function BackButton() {
  const { t } = useTranslation()
  const [showBackButton, setShowBackButton] = React.useState(false)

  React.useEffect(() => {
    const handleShowBackButton = () => setShowBackButton(true)
    const handleHideBackButton = () => setShowBackButton(false)

    // Listen for both forgot password success AND reset password views
    window.addEventListener('forgot-password-success-show', handleShowBackButton)
    window.addEventListener('forgot-password-success-hide', handleHideBackButton)
    window.addEventListener('reset-password-show', handleShowBackButton)
    window.addEventListener('reset-password-hide', handleHideBackButton)

    return () => {
      window.removeEventListener('forgot-password-success-show', handleShowBackButton)
      window.removeEventListener('forgot-password-success-hide', handleHideBackButton)
      window.removeEventListener('reset-password-show', handleShowBackButton)
      window.removeEventListener('reset-password-hide', handleHideBackButton)
    }
  }, [])

  const handleClick = () => {
    // Dispatch events for both forms to listen to
    window.dispatchEvent(new Event('forgot-password-back-clicked'))
    window.dispatchEvent(new Event('reset-password-back-clicked'))
  }

  if (!showBackButton) {
    return null
  }

  return (
    <button
      onClick={handleClick}
      className="flex items-center gap-1 text-[length:var(--text-sm-font-size)] font-[var(--font-weight-medium)] text-[var(--colors-violet-800)] hover:text-violet-900 bg-transparent border-0 cursor-pointer px-4"
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      <span>{t('auth.forgotPassword.back')}</span>
    </button>
  )
}

// Signals main process after React mount + paint so the window shows without flash.
function ReadySignal() {
  React.useEffect(() => {
    // useEffect = React has committed to DOM.
    // Double rAF = browser has painted at least one styled frame.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.api?.send?.('renderer-ready')
      })
    })
  }, [])
  return null
}

ReactDOM.createRoot(document.getElementById('app') as HTMLElement).render(
  <React.StrictMode>
    <ModeProvider>
      <LanguageProvider>
        <DisplayModeProvider>
          <WindowContextProvider
            titlebar={{
              icon: <SmileyIcon size={24} />,
            }}
          >
            <ErrorBoundary variant="page" name="App">
              <App />
            </ErrorBoundary>
            <ReadySignal />
          </WindowContextProvider>
        </DisplayModeProvider>
      </LanguageProvider>
    </ModeProvider>
  </React.StrictMode>
)
